import { API_HOST } from './constants.js';
import { getApiHost } from './apiHost.js';

export async function fetchPlayersByStatscoreIds(ids) {
    if (!ids || ids.length === 0) return [];

    try {
        // const baseUrl = typeof window !== 'undefined' && window.location.hostname === 'localhost'
        //     ? '/api/v1/entities'
        //     : 'https://footballinsider247-madrid.qtstage.io/api/v1/entities';
        const apiHost = getApiHost();
        const baseUrl = `${apiHost}/api/v1/entities`;
        const params = new URLSearchParams();
        params.append('type', 'player');

        const fetchPromises = ids.map(async (id) => {
            const url = `${baseUrl}?type=player&attr_statscore_player_id=${id}`;
            const res = await fetch(url);
            const data = await res.json();
            return data.entities?.[0] || null;
        });

        const results = await Promise.all(fetchPromises);
        return results.filter(p => p !== null);
    } catch (err) {
        console.error('Error fetching Quintype entities:', err);
        return [];
    }
}

// Page through EVERY player entity once and return a { [statscoreId]: photoUrl }
// index (only players that actually have a photo are included).
//
// Why: the World Cup aggregator needs photos for all squad players at once
// (~1300 ids). Resolving them via fetchPlayersByStatscoreIds fires one request
// per id, which blows Cloudflare's per-invocation subrequest limit (the cold
// assemble then fails and the KV write never commits). The entities API has no
// multi-id filter, so we page the full list instead: ~9 requests (limit 1000)
// regardless of how many players are needed. Best-effort — returns whatever it
// gathered even if a page fails.
export async function fetchAllPlayerPhotos() {
    const baseUrl = `https://www.footballinsider247.com/api/v1/entities`;
    const PAGE = 1000;
    const photoById = {};
    try {
        let offset = 0;
        for (;;) {
            const url = `${baseUrl}?type=player&limit=${PAGE}&offset=${offset}`;
            const res = await fetch(url);
            const data = await res.json();
            const entities = data.entities || [];
            for (const entity of entities) {
                const sid = entity.statscore_player_id || entity.metadata?.['statscore-player-id'];
                if (!sid) continue;
                const photoUrl = Array.isArray(entity.photo) && entity.photo.length > 0
                    ? entity.photo[0].url
                    : typeof entity.photo === 'string' && entity.photo ? entity.photo : null;
                if (photoUrl) photoById[String(sid)] = photoUrl;
            }
            offset += PAGE;
            if (entities.length < PAGE || offset >= (data.total || 0)) break;
        }
    } catch (err) {
        console.error('Error fetching player photo index:', err);
    }
    return photoById;
}

export async function fetchTopValuedPlayersBySeason(seasonId, position) {
    try {
        const apiHost = API_HOST;
        const baseUrl = `${apiHost}/api/v1/entities`;
        const url = `${baseUrl}?type=player&attr_statscore_season_id=${seasonId}&limit=100`;
        const res = await fetch(url);
        const data = await res.json();
        const entities = data.entities || [];

        // Filter by position (broad match for Goalkeeper, Defender, etc.)
        const filtered = entities.filter(e =>
            e.position?.toLowerCase().includes(position.toLowerCase()) ||
            position.toLowerCase().includes(e.position?.toLowerCase() || '')
        );

        // Sort by base_value descending and take top 3
        const top3 = filtered
            .sort((a, b) => (parseFloat(b.base_value) || 0) - (parseFloat(a.base_value) || 0))
            .slice(0, 3);

        return top3.map(e => ({
            id: e.statscore_player_id || e.metadata?.statscore_player_id,
            name: e.name,
            value: e.base_value || 0
        }));
    } catch (err) {
        console.error('Error fetching top valued players:', err);
        return [];
    }
}

export async function fetchStoriesByTag(tagSlug) {
    try {
        const apiHost = API_HOST;
        const PAGE_SIZE = 20;
        const baseUrl = `${apiHost}/api/v1/advanced-search?tag-name-lower=${encodeURIComponent(tagSlug)}&limit=${PAGE_SIZE}&sort=latest-published`;

        // First page
        const firstRes = await fetch(baseUrl);
        if (!firstRes.ok) throw new Error(`Advanced search failed: ${firstRes.status}`);
        const firstData = await firstRes.json();
        const total = firstData.total || 0;
        let stories = firstData.items || [];

        // Fetch remaining pages in parallel if total > PAGE_SIZE
        if (total > PAGE_SIZE) {
            const offsets = [];
            for (let offset = PAGE_SIZE; offset < total; offset += PAGE_SIZE) {
                offsets.push(offset);
            }
            const pageResults = await Promise.all(
                offsets.map(offset =>
                    fetch(`${baseUrl}&offset=${offset}`)
                        .then(r => r.ok ? r.json() : { items: [] })
                        .then(d => d.items || [])
                )
            );
            stories = stories.concat(...pageResults);
        }

        return stories.map(story => {
            const imageKey = story['hero-image-s3-key'];
            const thumbnail = `https://media.assettype.com/${imageKey}?auto=format&fit=crop&w=900&q=80`;

            return {
                id: story.id,
                headline: story.headline,
                date: new Date(story['published-at']).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
                category: story.sections?.[0]?.name,
                thumbnail,
                url: story.url,
            };
        });
    } catch (err) {
        console.error('Error fetching stories by tag:', err);
        return [];
    }
}

const normalize = (val) => {
    if (!val) return '';
    return val
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-');
};

const cleanClubName = (name) => {
    if (!name) return '';
    return name.replace(/\s*(Football Club|FC|LFC|AFC|Association Football Club)\b/gi, '').trim();
};

export async function fetchStoriesWithCollectionFallback(collectionSlug, entityId, tabSlug, clubSlug = '', clubName = '') {
    const baseUrl = API_HOST;
    const activeTab = tabSlug ? tabSlug.toLowerCase().trim() : 'transfers';

    const tabSlugMap = {
        matches: "match",
        nextmanager: "next-manager",
        transfers: "transfers",
        injuries: "injuries",
        finance: "finance",
    };

    const mappedTab = tabSlugMap[activeTab.replace(/\s+/g, '')] || activeTab;

    try {
        // 1. Fetch config to get sections
        let sections = [];
        try {
            const configRes = await fetch(`${baseUrl}/api/v1/config`);
            if (configRes.ok) {
                const configData = await configRes.json();
                sections = configData.sections || [];
            }
        } catch (err) {
            console.error('Error fetching /api/v1/config:', err);
        }

        // 2. Find the club section
        const targetClubSlug = normalize(clubSlug);
        const targetClubName = normalize(cleanClubName(clubName));

        const clubSection = sections.find(s => {
            if (!s) return false;
            const sSlug = normalize(s.slug);
            const sName = normalize(s.name);
            return (targetClubSlug && (sSlug === targetClubSlug || sName === targetClubSlug)) ||
                (targetClubName && (sSlug === targetClubName || sName === targetClubName));
        });

        // 3. Find the sections whose parent-id is the id of the club section
        const childSections = clubSection
            ? sections.filter(s => s['parent-id'] === clubSection.id)
            : [];

        // 4. Match child section using normalized slug/name or fallback formats
        const normalizedMappedTab = normalize(mappedTab);
        const matchedSection = childSections.find(s => {
            if (!s) return false;
            const sSlug = normalize(s.slug);
            const sName = normalize(s.name);
            const pSlug = normalize(clubSection?.slug);

            return sSlug === normalizedMappedTab ||
                sName === normalizedMappedTab ||
                sSlug === `${pSlug}-${normalizedMappedTab}` ||
                sSlug === `${normalizedMappedTab}-${pSlug}` ||
                sSlug.endsWith(`-${normalizedMappedTab}`);
        });

        console.log("clubSection", clubSection);
        console.log("childSections", childSections);
        console.log("activeTab", activeTab);
        console.log("mappedTab", mappedTab);
        console.log("matchedSection", matchedSection);

        // 5. Build request URL using section-id if matched, else fallback to section-slug
        let url = '';
        if (matchedSection) {
            url = `${baseUrl}/api/v1/stories?section-id=${matchedSection.id}`;
            console.log("Fetching by section-id URL:", url);
        } else {
            url = `${baseUrl}/api/v1/stories?section-slug=${normalizedMappedTab}`;
            console.log("Fetching by section-slug URL:", url);
        }

        const storiesRes = await fetch(url);
        if (!storiesRes.ok) {
            throw new Error(`Stories API responded with status ${storiesRes.status}`);
        }
        const storiesData = await storiesRes.json();

        // 7. Parse the stories from the response
        let rawStories = [];
        if (storiesData && storiesData.stories) {
            rawStories = storiesData.stories;
        } else if (storiesData && storiesData.items) {
            rawStories = storiesData.items.map(item => {
                if (item.type === 'story' && item.story) {
                    return item.story;
                }
                if (item.headline) {
                    return item;
                }
                return null;
            }).filter(Boolean);
        } else if (Array.isArray(storiesData)) {
            rawStories = storiesData;
        }

        return rawStories.map(story => {
            const imageKey = story['hero-image-s3-key'] ||
                story['hero-image-metadata']?.['s3-key'] ||
                story['hero-image-metadata']?.['key'];

            const thumbnail = story['hero-image-metadata']?.['original-url'] ||
                (imageKey ? `https://media.assettype.com/${imageKey}?auto=format&fit=crop&w=900&q=80` : null);

            // Prioritize the section name matching this club or its child subsections for the category badge
            let categoryName = 'NEWS';
            if (story.sections && story.sections.length > 0) {
                // First try to find a specific child sub-hub section (like Transfer Hub or Injury Hub)
                let clubRelatedSection = story.sections.find(sec =>
                    clubSection && sec['parent-id'] === clubSection.id
                );
                // Fallback to the main club section itself if no child section is tagged
                if (!clubRelatedSection) {
                    clubRelatedSection = story.sections.find(sec =>
                        clubSection && sec.id === clubSection.id
                    );
                }
                categoryName = clubRelatedSection ? clubRelatedSection.name : story.sections[0].name;
            }

            return {
                id: story.id,
                headline: story.headline,
                date: story['published-at']
                    ? new Date(story['published-at']).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '',
                category: categoryName,
                thumbnail: thumbnail,
                url: story.url || `/${story.slug}`
            };
        });
    } catch (err) {
        console.error('Error fetching fallback stories:', err);
        return [];
    }
}

export async function fetchStoriesByPlayerName(name) {
    try {
        let cleanName = name || '';
        console.log(cleanName, "----clean name")
        
        // Convert dashes to spaces for slug format
        cleanName = cleanName.replace(/-/g, ' ').trim();
        
        const query = encodeURIComponent(cleanName.toLowerCase());
        const apiHost = API_HOST;
        const url = `${apiHost}/api/v1/advanced-search?tag-name-lower=${query}&sort=latest-published`;
        const res = await fetch(url);
        const data = await res.json();
        let stories = data.items || [];

        // Map and sort by published date (latest first)
        const mappedStories = stories.map(story => {
            const imageKey = story['hero-image-s3-key'] ||
                story['hero-image-metadata']?.['s3-key'] ||
                story['hero-image-metadata']?.['key'];

            // Prioritize original-url if available (especially for imported stories)
            const thumbnail = story['hero-image-metadata']?.['original-url'] ||
                (imageKey ? `https://media.assettype.com/${imageKey}?auto=format&fit=crop&w=900&q=80` : null);

            const publishedDate = new Date(story['last-published-at']);
            return {
                id: story.id,
                headline: story.headline,
                date: publishedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
                publishedAt: publishedDate.getTime(), // timestamp for sorting
                category: story.sections?.[0]?.name || 'NEWS',
                thumbnail: thumbnail,
                url: story.url || `${apiHost}/${story.slug}`
            };
        });

        // Sort by latest published date (descending)
        return mappedStories.sort((a, b) => b.publishedAt - a.publishedAt);
    } catch (err) {
        console.error('Error fetching player stories:', err);
        return [];
    }
}

export async function fetchClubEntityByStatscoreId(statscoreId) {
    if (!statscoreId) return null;
    try {
        const url = `${API_HOST}/api/v1/entities?type=club`;
        const res = await fetch(url);
        const data = await res.json();
        const entities = data.entities || [];

        // Find club by statscore_participant_id
        return entities.find(e => String(e.statscore_participant_id) === String(statscoreId)) || null;
    } catch (err) {
        console.error('Error fetching club entity:', err);
        return null;
    }
}

export async function fetchWorldCupArticles() {
    try {
        const query = encodeURIComponent("worldcup 2026");
        const apiHost = API_HOST;
        const url = `${apiHost}/api/v1/advanced-search?tag-name-lower=${query}&sort=latest-published`;
        const res = await fetch(url);
        const data = await res.json();
        let stories = data.items || [];

        return stories.map(story => {
            const imageKey = story["hero-image-s3-key"] ||
                story["hero-image-metadata"]?.["s3-key"] ||
                story["hero-image-metadata"]?.["key"];

            const thumbnail = story["hero-image-metadata"]?.["original-url"] ||
                (imageKey ? `https://media.assettype.com/${imageKey}?auto=format&fit=crop&w=900&q=80` : null);

            const publishedDate = new Date(story["last-published-at"] || story["published-at"]);
            return {
                id: story.id,
                headline: story.headline,
                date: publishedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }),
                category: story.sections?.[0]?.name || "NEWS",
                thumbnail: thumbnail,
                url: story.url || `${apiHost}/${story.slug}`
            };
        });
    } catch (err) {
        console.error("Error fetching world cup articles:", err);
        return [];
    }
}
